import { useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { 
  XMarkIcon,
  HomeIcon,
  UsersIcon,
  CreditCardIcon,
  ArrowDownTrayIcon,
  BanknotesIcon,
  TagIcon,
  DocumentArrowDownIcon,
  Cog6ToothIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { useResponsive } from '@/contexts/ResponsiveContext'
import useAuthStore from '@/stores/authStore'

const navigation = [
  { name: '대시보드', href: '/dashboard', icon: HomeIcon },
  { name: '사용자 관리', href: '/dashboard/users', icon: UsersIcon },
  { name: '거래 내역', href: '/dashboard/transactions', icon: CreditCardIcon },
  { name: '출금 관리', href: '/dashboard/withdrawals', icon: ArrowDownTrayIcon },
  { name: '스테이킹 관리', href: '/dashboard/staking', icon: BanknotesIcon },
  { name: '담당자코드', href: '/dashboard/referral-codes', icon: TagIcon },
  { name: '데이터 내보내기', href: '/dashboard/exports', icon: DocumentArrowDownIcon },
  { name: '설정', href: '/dashboard/settings', icon: Cog6ToothIcon },
]

const Sidebar = () => {
  const location = useLocation()
  const { user } = useAuthStore()
  const { 
    isMobile, 
    isDesktop,
    sidebarOpen,
    setSidebarOpen
  } = useResponsive()

  // Close sidebar on route change for mobile
  useEffect(() => {
    if (!isMobile) {
      return
    }

    setSidebarOpen(false)
  }, [location.pathname, isMobile, setSidebarOpen])

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center flex-shrink-0 h-16 px-4 bg-primary-600">
        <h1 className="text-lg font-semibold text-white">
          관리자 패널
        </h1>
        {/* Close button for mobile */}
        {isMobile && (
          <button
            type="button"
            className="ml-auto p-2 rounded-md text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-white"
            onClick={() => setSidebarOpen(false)}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <div className="flex-1 flex flex-col overflow-y-auto">
        <nav className="flex-1 px-2 py-4 bg-white space-y-1">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href || 
              (item.href !== '/dashboard' && location.pathname.startsWith(item.href))
            
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={clsx(
                  'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-all duration-200',
                  // Touch-friendly sizing for mobile
                  isMobile ? 'min-h-[48px] px-3 py-3 text-base' : 'min-h-[40px]',
                  // Active/inactive states
                  isActive
                    ? 'bg-primary-100 text-primary-900 shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 active:bg-gray-100'
                )}
                onClick={() => {
                  if (isMobile) {
                    setSidebarOpen(false)
                  }
                }}
              >
                <item.icon
                  className={clsx(
                    'flex-shrink-0 transition-colors duration-200',
                    isMobile ? 'mr-4 h-6 w-6' : 'mr-3 h-5 w-5',
                    isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                  )}
                />
                <span className="truncate">{item.name}</span>
              </NavLink>
            )
          })}
        </nav>
      </div>

      {/* User info */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 p-4">
        <div className="flex items-center w-full">
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-primary-500 flex items-center justify-center">
              <span className="text-sm font-medium text-white">
                {user?.name?.charAt(0)?.toUpperCase() || 'A'}
              </span>
            </div>
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-700 truncate">
              {user?.nickname || '관리자'}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {user?.role === 'admin' ? '관리자' : '사용자'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )

  if (isDesktop) {
    // Fixed sidebar for desktop
    return (
      <div className="fixed inset-y-0 left-0 z-sidebar w-64 bg-white shadow-lg">
        <SidebarContent />
      </div>
    )
  }

  // Mobile overlay sidebar
  if (sidebarOpen) {
    return (
      <>
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[1025]"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 z-[1035] w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out">
          <SidebarContent />
        </div>
      </>
    )
  }

  return null
}

export default Sidebar